import { Injectable, computed, effect, signal } from '@angular/core';
import {
  type ColorSchemePreference,
  applySystemColorScheme,
  persistColorSchemePreference,
  readStoredColorSchemePreference,
  readSystemPrefersDark,
  resolveColorSchemeIsDark,
} from './system-color-scheme';

const MEDIA_QUERY = '(prefers-color-scheme: dark)';

@Injectable({ providedIn: 'root' })
export class ColorSchemeService {
  private readonly preference = signal<ColorSchemePreference>('system');
  private readonly systemPrefersDark = signal(false);
  private mediaCleanup: (() => void) | null = null;

  readonly isDark = computed(() =>
    resolveColorSchemeIsDark(this.preference(), this.systemPrefersDark()),
  );

  constructor() {
    effect(() => {
      applySystemColorScheme(this.isDark());
    });
  }

  init(): void {
    this.systemPrefersDark.set(readSystemPrefersDark());
    this.preference.set(readStoredColorSchemePreference());
    this.bindSystemPreferenceListener();
  }

  toggle(): void {
    this.setPreference(this.isDark() ? 'light' : 'dark');
  }

  setPreference(preference: ColorSchemePreference): void {
    this.preference.set(preference);
    persistColorSchemePreference(preference);
  }

  private bindSystemPreferenceListener(): void {
    this.mediaCleanup?.();

    if (typeof window === 'undefined') {
      return;
    }

    const media = window.matchMedia(MEDIA_QUERY);
    const onChange = (): void => {
      this.systemPrefersDark.set(media.matches);
    };
    media.addEventListener('change', onChange);
    this.mediaCleanup = () => media.removeEventListener('change', onChange);
  }
}
